import { useEffect, useState } from 'react';

import { getSiteConfig, getUrlByRouteRole, useIntl } from '@openedx/frontend-base';
import {
  Form,
  Icon,
  Spinner,
  StatefulButton,
  Tab,
  Tabs,
} from '@openedx/paragon';
import { ChevronLeft } from '@openedx/paragon/icons';
import { Helmet } from 'react-helmet';
import { useNavigate, useParams } from 'react-router-dom';

import BaseContainer from '../base-container';
import { validatePassword } from './data/api';
import { useResetPassword, useValidateToken } from './data/apiHook';
import {
  FORM_SUBMISSION_ERROR, PASSWORD_RESET, PASSWORD_VALIDATION_ERROR, TOKEN_STATE,
} from './data/constants';
import messages from './messages';
import ResetPasswordFailure from './ResetPasswordFailure';
import { PasswordField } from '../common-components';
import { loginPath, loginRole, resetPasswordRole } from '../constants';
import { LETTER_REGEX, NUMBER_REGEX } from '../data/constants';
import { getAllPossibleQueryParams, updatePathWithQueryParams, windowScrollTo } from '../data/utils';
import { RegisterProvider } from '../register/components/RegisterContext';

const ResetPasswordPageInner = () => {
  const { formatMessage } = useIntl();
  const newPasswordError = formatMessage(messages['password.validation.message']);
  const { token } = useParams();
  const navigate = useNavigate();

  const [status, setStatus] = useState(TOKEN_STATE.PENDING);
  const [validatedToken, setValidatedToken] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [errorCode, setErrorCode] = useState(null);

  // React Query hooks
  const { mutate: validateResetToken } = useValidateToken();
  const { mutate: resetUserPassword, isPending: isResetting } = useResetPassword();

  useEffect(() => {
    if (status !== TOKEN_STATE.PENDING) {
      setErrorCode(status);
    }
    if (status === PASSWORD_VALIDATION_ERROR) {
      setFormErrors({ newPassword: newPasswordError });
    }
  }, [status, newPasswordError]);

  useEffect(() => {
    if (token && status === TOKEN_STATE.PENDING) {
      validateResetToken(token, {
        onSuccess: (data) => {
          const { is_valid: isValid, token: tokenValue } = data;
          if (isValid) {
            setStatus(TOKEN_STATE.VALID);
            setValidatedToken(tokenValue);
          } else {
            setStatus(PASSWORD_RESET.INVALID_TOKEN);
          }
        },
        onError: (error) => {
          if (error.response?.status === 429) {
            setStatus(PASSWORD_RESET.FORBIDDEN_REQUEST);
          } else {
            setStatus(PASSWORD_RESET.INTERNAL_SERVER_ERROR);
          }
        },
      });
    }
  }, [token, status, validateResetToken]);

  const validatePasswordFromBackend = async (password) => {
    let errorMessage = '';
    try {
      const payload = {
        reset_password_page: true,
        password,
      };
      errorMessage = await validatePassword(payload);
    } catch (err) {
      errorMessage = '';
    }
    setFormErrors({ ...formErrors, newPassword: errorMessage });
  };

  const validateInput = (name, value) => {
    let fieldError = '';
    switch (name) {
      case 'newPassword':
        if (!value || !LETTER_REGEX.test(value) || !NUMBER_REGEX.test(value) || value.length < 8) {
          fieldError = formatMessage(messages['password.validation.message']);
        } else {
          validatePasswordFromBackend(value);
        }
        break;
      case 'confirmPassword':
        if (!value) {
          fieldError = formatMessage(messages['confirm.your.password']);
        } else if (value !== newPassword) {
          fieldError = formatMessage(messages['passwords.do.not.match']);
        }
        break;
      default:
        break;
    }
    setFormErrors((prev) => ({ ...prev, [name]: fieldError }));
    return !fieldError;
  };

  const handleOnBlur = (event) => {
    const { name, value } = event.target;
    validateInput(name, value);
  };

  const handleConfirmPasswordChange = (e) => {
    const { value } = e.target;

    setConfirmPassword(value);
    validateInput('confirmPassword', value);
  };

  const handleOnFocus = (e) => {
    setFormErrors((prev) => ({ ...prev, [e.target.name]: '' }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const isPasswordValid = validateInput('newPassword', newPassword);
    const isPasswordConfirmed = validateInput('confirmPassword', confirmPassword);

    if (isPasswordValid && isPasswordConfirmed) {
      const formPayload = {
        new_password1: newPassword,
        new_password2: confirmPassword,
      };
      const params = getAllPossibleQueryParams();
      resetUserPassword({ formPayload, token: validatedToken, params }, {
        onSuccess: (data) => {
          const { reset_status: resetStatus } = data;
          if (resetStatus) {
            setStatus('success');
          }
        },
        onError: (error) => {
          const data = error.response?.data;
          const { token_invalid: tokenInvalid, err_msg: resetErrors } = data || {};
          if (tokenInvalid) {
            setStatus(PASSWORD_RESET.INVALID_TOKEN);
          } else {
            setStatus(PASSWORD_VALIDATION_ERROR);
            setErrorMsg(resetErrors);
          }
        },
      });
    } else {
      setErrorCode(FORM_SUBMISSION_ERROR);
      windowScrollTo({ left: 0, top: 0, behavior: 'smooth' });
    }
  };

  const tabTitle = (
    <div className="d-inline-flex flex-wrap align-items-center">
      <Icon src={ChevronLeft} />
      <span className="ml-2">{formatMessage(messages['sign.in'])}</span>
    </div>
  );

  useEffect(() => {
    if (status === PASSWORD_RESET.INVALID_TOKEN) {
      navigate(updatePathWithQueryParams(getUrlByRouteRole(resetPasswordRole)), { state: { status } });
    }
    if (status === 'success') {
      navigate(updatePathWithQueryParams(getUrlByRouteRole(loginRole)), { state: { showResetPasswordSuccessBanner: true } });
    }
  }, [status, navigate]);

  if (status === TOKEN_STATE.PENDING) {
    return <Spinner animation="border" variant="primary" className="spinner--position-centered" />;
  }

  return (
    <BaseContainer>
      <div>
        <Helmet>
          <title>
            {formatMessage(messages['reset.password.page.title'], { siteName: getSiteConfig().siteName })}
          </title>
        </Helmet>
        <Tabs activeKey="" id="controlled-tab" onSelect={() => navigate(updatePathWithQueryParams(getUrlByRouteRole(loginRole)))}>
          <Tab title={tabTitle} eventKey={loginPath} />
        </Tabs>
        <div id="main-content" className="main-content">
          <div className="mw-xs">
            <ResetPasswordFailure errorCode={errorCode} errorMsg={errorMsg} />
            <h4>{formatMessage(messages['reset.password'])}</h4>
            <p className="mb-4">{formatMessage(messages['reset.password.page.instructions'])}</p>
            <Form id="set-reset-password-form" name="set-reset-password-form">
              <PasswordField
                name="newPassword"
                value={newPassword}
                handleChange={(e) => setNewPassword(e.target.value)}
                handleBlur={handleOnBlur}
                handleFocus={handleOnFocus}
                errorMessage={formErrors.newPassword}
                floatingLabel={formatMessage(messages['new.password.label'])}
              />
              <PasswordField
                name="confirmPassword"
                value={confirmPassword}
                handleChange={handleConfirmPasswordChange}
                handleFocus={handleOnFocus}
                errorMessage={formErrors.confirmPassword}
                showRequirements={false}
                floatingLabel={formatMessage(messages['confirm.password.label'])}
              />
              <StatefulButton
                id="submit-new-password"
                name="submit-new-password"
                type="submit"
                variant="brand"
                className="reset-password--button"
                state={isResetting ? 'pending' : 'default'}
                labels={{
                  default: formatMessage(messages['reset.password']),
                  pending: '',
                }}
                onClick={e => handleSubmit(e)}
                onMouseDown={(e) => e.preventDefault()}
              />
            </Form>
          </div>
        </div>
      </div>
    </BaseContainer>
  );
};

const ResetPasswordPage = (props) => (
  <RegisterProvider>
    <ResetPasswordPageInner {...props} />
  </RegisterProvider>
);

export default ResetPasswordPage;
